<template>
  <el-form-item
    :id="erpHotPointId"
    :operation="operationLabel"
    :prop="option.propertyName"
    :rules="option.attr && option.attr.rules">
    <!-- slot label -->
    <el-dropdown
      slot="label"
      :trigger="option.trigger || 'click'"
      :placement="option.placement || 'bottom-start'"
      @command="handleMenuClick">
      <div class="el-dropdown-link">
        <span class="theme">{{ option.type === 'rangeMenu' ? option.label : item.label }}</span>
        <formatter
          v-if="option.menuFormatterLabel"
          :formatter="option.menuFormatterLabel"
          :option="option"
          :model="model" />
        <i class="layout layout-caretdown"></i>
      </div>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item
          v-for="(opt, i) in option.options"
          :key="opt.propertyName+i"
          :class="{ 'is-active': option.type === 'menu' && opt.label == item.label }"
          :command="opt">
          {{ opt.label }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
    <!-- slot default -->
    <formatter v-if="item.formatter" :option="item" :model="model" />
    <el-input
      v-else-if="item.type === 'dept'"
      :style="item.width ? {width: item.width} : null"
      :placeholder="item.placeholder"
      readonly
      v-bind="item.attr"
      :value="showDeptLabel"
      @click.native="openDeptTree(item)"
      @focus="focus = true"
      @blur="focus = false"
      @mouseenter.native="focus = true"
      @mouseleave.native="focus = false"
      data-autocomplet="off"
      >
      <i
        slot="suffix"
        v-show="focus && showDeptLabel"
        class="el-input__icon el-icon-circle-close el-input__clear"
        @click.stop="clearDept">
      </i>
    </el-input>
    <kye-dept-select
      :style="item.width ? {width: item.width} : null"
      v-else-if="item.type === 'deptSelect'"
      :placeholder="item.placeholder"
      v-bind="item.attr"
      v-model="model[item.propertyName]"
      @change="deptSelectChange">
    </kye-dept-select>
    <kye-number
      v-else-if="item.type === 'number'"
      :style="item.width ? {width: item.width} : null"
      :placeholder="item.placeholder"
      :symbol="option.type === 'rangeMenu' ? item.label : ''"
      v-bind="item.attr"
      :value="bindValue(option.type === 'rangeMenu' ? option.propertyName: item.propertyName)"
      @input="menuChange">
    </kye-number>
    <el-input
      v-else-if="item.type === 'text'"
      :style="item.width ? {width: item.width} : null"
      clearable
      :placeholder="item.placeholder"
      v-bind="item.attr"
      :value="bindValue(option.type === 'rangeMenu' ? option.propertyName: item.propertyName)"
      @input="menuChange">
      <template v-if="option.type === 'rangeMenu'" slot="prepend">{{ item.label }}</template>
    </el-input>
    <kye-select
      v-else-if="item.type === 'select'"
      :style="item.width ? {width: item.width} : null"
      clearable
      :placeholder="item.placeholder"
      v-bind="item.attr"
      :value="bindValue(item.propertyName)"
      :labels="selectedLabels"
      :key="item.propertyName + 'Select'"
      @input="bindChange"
      @visible-change="selectVisibleChange">
      <kye-option
        v-for="(opt, i) in item.options"
        :key="opt.value+i"
        :disabled="opt.disabled"
        :label="opt.label || ' '"
        :value="opt.value">
      </kye-option>
    </kye-select>
    <kye-select
      v-else-if="item.type === 'remoteSelect'"
      :style="item.width ? {width: item.width} : null"
      :placeholder="item.placeholder"
      clearable
      filterable
      remote
      :remote-method="remoteMethod"
      :loading="item.loading"
      :user="item.user"
      v-bind="item.attr"
      :value="bindValue(item.propertyName)"
      :labels="selectedLabels"
      ref="remoteSelect"
      :key="item.propertyName + 'RemoteSelect'"
      @input="bindChange"
      @visible-change="selectVisibleChange">
      <i slot="prefix" class="el-icon-search" style="padding-top:8px;padding-left:4px;"></i>
      <kye-option
        v-for="(opt, i) in item.options"
        :class="opt.optionDisabled ? 'kyeuseroff' : ''"
        :key="opt.value+i"
        :label="opt.label"
        :value="opt.value">
      </kye-option>
    </kye-select>
    <el-autocomplete
      v-else-if="item.type === 'autocomplete'"
      ref="autocomplete"
      popper-class="kye-popper-auto"
      :style="item.width ? {width: item.width} : null"
      :placeholder="item.placeholder"
      :trigger-on-focus="false"
      :fetch-suggestions="fetchSuggestions"
      v-bind="item.attr"
      :value="bindValue(item.propertyName)"
      @input="bindChange"
      @select="bindSelect">
      <span slot-scope="scope" :class="scope.item.optionDisabled ? 'kyeuseroff' : ''">{{scope.item.label}}</span>
    </el-autocomplete>
    <el-row
      v-else-if="item.type === 'range'"
      :gutter="8"
      :style="item.width ? {width: item.width} : null">
      <el-col
        v-for="(opt, i) in item.options"
        :key="opt.propertyName+i"
        :span="24 / item.options.length">
        <kye-number
          v-if="opt.type === 'number'"
          :symbol="opt.prepend"
          :unit="opt.append"
          v-bind="opt.attr"
          :value="bindValue(opt.propertyName)"
          @input="e => bindChange(e, opt)">
        </kye-number>
        <el-input
          v-else
          clearable
          :placeholder="opt.placeholder"
          v-bind="opt.attr"
          :value="bindValue(opt.propertyName)"
          @input="e => bindChange(e, opt)">
          <template v-if="opt.prepend" slot="prepend">{{ opt.prepend }}</template>
          <template v-if="opt.append" slot="append">{{ opt.append }}</template>
        </el-input>
      </el-col>
    </el-row>
    <!-- 日期组件：v-if 会导致弹框定位失效 -->
    <kye-date-picker
      v-for="opt in option.options.filter(v => v.type === 'datePicker')"
      v-show="item.propertyName === opt.propertyName"
      :style="opt.width ? {width: opt.width} : null"
      :key="opt.propertyName"
      :placeholder="opt.placeholder"
      :type="opt.dateType || 'date'"
      :format="opt.format || 'yyyy-MM-dd'"
      :format2="opt.format2"
      :value-format="opt.valueFormat || 'yyyy-MM-dd HH:mm:ss'"
      :operation="opt.operation"
      v-bind="opt.attr"
      :value="bindValue(opt.propertyName)"
      @input="e => datePickerChange(e, opt)">
    </kye-date-picker>
    <kye-date-picker3
      v-for="opt in option.options.filter(v => v.type === 'datePicker3')"
      v-show="item.propertyName === opt.propertyName"
      :style="opt.width ? {width: opt.width} : null"
      :key="opt.propertyName"
      :placeholder="opt.placeholder"
      :type="opt.dateType"
      :format="opt.format"
      :format2="opt.format2"
      :value-format="opt.valueFormat"
      :operation="opt.operation"
      v-bind="opt.attr"
      :value="bindValue(opt.propertyName)"
      @input="e => datePickerChange(e, opt)">
    </kye-date-picker3>
    <kye-date-picker2
      v-for="opt in option.options.filter(v => v.type === 'datePicker2')"
      v-show="item.propertyName === opt.propertyName"
      :style="opt.width ? {width: opt.width} : null"
      :key="opt.propertyName"
      :placeholder="opt.placeholder"
      :startPlaceholder="opt.startPlaceholder"
      :endPlaceholder="opt.endPlaceholder"
      :type="opt.dateType"
      :format="opt.format"
      :value-format="opt.valueFormat"
      :operation="opt.operation"
      v-bind="opt.attr"
      :value="bindValue(opt.propertyName)"
      @input="e => datePickerChange(e, opt)">
    </kye-date-picker2>
    <el-time-picker
      v-for="opt in option.options.filter(v => v.type === 'timePicker')"
      v-show="item.propertyName === opt.propertyName"
      :style="opt.width ? {width: opt.width} : null"
      :key="opt.propertyName"
      :clearable="false"
      :is-range="opt.dateType === 'range'"
      :format="opt.format || 'HH:mm:ss'"
      :value-format="opt.valueFormat || 'HH:mm:ss'"
      v-bind="opt.attr"
      :value="bindValue(opt.propertyName)"
      @input="e => datePickerChange(e, opt)">
    </el-time-picker>
  </el-form-item>
</template>

<script>
  import mixins from '../../mixins'
  import Formatter from './form-item-formatter'
  import {
    getModelValue,
    setModelValue
  } from '../../utils'
  import {
    OPERATION_LABELS,
    perfectField,
    getRemoteOptions,
    remoteSelectMethod,
    navigateSelectFirstOption
  } from './utils'

  export default {
    name: 'form-item-menu',
    mixins: [mixins],
    components: { Formatter },
    props: {
      option: Object,
      model: Object,
      fields: Array,
      XPathHash: String,
      index: [String, Number],
    },
    data () {
      return {
        item: {},
        focus: false,
      }
    },
    created () {
      this.initOption()
      this.changeMenuItemByIndex(this.index)
    },
    computed: {
      erpHotPointId() {
        return 'ERP_HOT_' + this.XPathHash + '_' + this.item.propertyName
      },
      operationLabel () {
        return OPERATION_LABELS[this.item.operation] || ''
      },
      itemOptionsMap () {
        return Array.isArray(this.item.options) ? this.item.options.reduce((s, v) => {
          s[v.value] = v.label
          return s
        }, {}) : {}
      },
      // 获取多选下拉框选中的 labels，用于抓取导出查询参数的dom文本
      selectedLabels () {
        if (this.item.attr && this.item.attr.multiple) {
          let val = getModelValue(this.model, this.item.propertyName)
          if (Array.isArray(val)) {
            return JSON.stringify(val.map(v => this.itemOptionsMap[v]))
          }
          return ''
        }
        return ''
      },
      showDeptLabel () {
        let arr = this.item._queryValue
        if (arr && arr.length) {
          return arr.map(v => v.label).join()
        }
        return ''
      }
    },
    methods: {
      bindValue (key) {
        return getModelValue(this.model, key)
      },
      bindChange (value, opt = this.item) {
        if (value && typeof value === 'string') {
          value = value.trim()
        }
        let key = opt.propertyName
        setModelValue(value, this.model, key)

        if (opt.type !== 'deptSelect') {
          opt.change && opt.change({ key, value, model: this.model, fields: this.fields })
        }
      },
      deptSelectChange (data, opt = this.item) {
        const key = this.item.propertyName
        if (this.item.change) {
          this.item.change({ ...data, key, model: this.model, fields: this.fields })
        }
      },
      bindSelect (value) {
        let key = this.item.propertyName
        this.item.select && this.item.select({ key, value, model: this.model, fields: this.fields })
      },
      datePickerChange (val, opt) {
        let keys = opt.valueKey
        if (keys && Array.isArray(keys)) {
          this.mergeModelInArray(val, keys, this.model)
        }
        this.bindChange(val, opt)
      },
      menuChange (val) {
        let opt = this.option.type === 'rangeMenu' ? this.option : this.item
        this.bindChange(val, opt)
      },
      selectVisibleChange (val) {
        // 远程下拉框可见时，同步下拉框
        if (val && typeof this.item.remoteMethod === 'function') {
          if (this.item.type === 'remoteSelect') {
            // 远程搜索有默认的子选项
            this.item.options = this.item.lookupCode ? this.lookUpOptions[this.item.lookupCode] : []
            this.getRemoteOptions(this.item)
          } else if (this.item.type === 'select') {
            getRemoteOptions(this.item, this.model)
          }
        }
      },
      getRemoteOptions (item) {
        if (item.attr && item.attr.multiple && item.allSearchResult) {
          item.options = Object.keys(item.allSearchResult).reduce((prev, key) => {
            if (this.bindValue(item.propertyName).includes(key)) {
              const obj = item.allSearchResult[key]
              prev.push(obj)
            }
            return prev
          }, item.options)
        }
      },
      async remoteMethod (val) {
        let arr = await remoteSelectMethod(val, this.item)
        if (arr.length) {
          // 自动选中第一行
          this.$nextTick(_ => navigateSelectFirstOption(this.$refs.remoteSelect))
        }
      },
      async fetchSuggestions (val, cb) {
        let arr = await remoteSelectMethod(val, this.item)
        cb(arr)
        if (arr.length) {
          // 自动选中第一行
          this.$nextTick(_ => {
            let ref = this.$refs.autocomplete
            ref && ref.highlight && ref.highlight(0)
          })
        }
      },
      // 支持用户手动切换 menuItem 的选项
      changeMenuItemByIndex (index = 0) {
        const toNumberVal = Number(index)
        if (isNaN(toNumberVal)) {
          return
        }
        // 数据兼容处理
        index = index < 0 || index >= this.option.options.length ? 0 : index
        const item = this.option.options[index]
        const key = item.key
        this.item = item
        this.option.$index = index

        const value = getModelValue(this.model, key)
        const obj = { key, value, model: this.model }
        this.item.change && this.item.change(obj)
        this.$emit('menu-change', this.option, item)
      },
      handleMenuClick (menu) {
        let key = menu.propertyName
        let optKey = this.option.propertyName

        this.item = menu
        if (this.item.type === 'remoteSelect') {
          this.getRemoteOptions(this.item)
        }
        let index = this.option.options.findIndex(v => v.propertyName === key)
        this.option.$index = index === -1 ? 0 : index
        let value = getModelValue(this.model, this.option.type === 'rangeMenu' ? optKey : key)
        let obj = { key, value, model: this.model }
        this.option.change && this.option.change(obj)
        this.$emit('menu-change', this.option, menu)
      },
      setMenuValue (index = 0) {
        const toNumberVal = Number(index)
        if (isNaN(toNumberVal)) {
          return
        }
        // 数据兼容处理
        index = index < 0 || index >= this.option.options.length ? 0 : index
        const item = this.option.options[index]
        const key = item.key
        this.item = item
        this.option.$index = index

        const value = getModelValue(this.model, key)
        const obj = { key, value, model: this.model }
        this.item.change && this.item.change(obj)
      },
      initOption () {
        if (this.option && this.option.options) {
          this.option.options.forEach(v => {
            perfectField(this, v)
            if (v.type === 'dept') {
              this.$set(v, '_queryValue', null)
            }
          })
        }
      },
      mergeModelInArray (val, keys, model) {
        keys.forEach((v, i) => {
          setModelValue((val && val[i]), model, v)
        })
      },
      openDeptTree (field) {
        this.$emit('open-dept-tree', field)
      },
      clearDept () {
        let key = this.item.propertyName
        this.item._queryValue = null
        setModelValue(null, this.model, key)
        this.$set(this.model, `${key}_deptContainLeaf_`, null)
        this.$set(this.model, `_deptInfo_${key}`, null)
        let fn = this.item.change
        if (fn && typeof fn === 'function') {
          fn({ key, value: null, model: this.model })
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/public/components/query-table/form-item-menu.vue